import { tr, usePartnerTranslation } from "../../../lib/language";
import React from "react";
import { Check, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  FieldRow,
  SectionHeader,
  TextInput,
  TextArea,
  SelectField,
  CompactCheckList,
  BulletTextArea,
  RadioCards,
} from "./fields";
import { CityTextInput, LocationTagInput } from "./LocationInputs";
import {
  CONTACT_INFO_ERROR,
  containsContactInfo,
} from "../../../lib/productTextSafety";
import {
  TITLE_MIN,
  TITLE_MAX,
  SHORT_DESC_MIN,
  SHORT_DESC_MAX,
  FULL_DESC_MIN,
  FULL_DESC_MAX,
  HIGHLIGHT_COUNT,
  HIGHLIGHT_MIN,
  HIGHLIGHT_MAX,
  VISIT_LOCATIONS_MIN,
  VISIT_LOCATIONS_MAX,
  KEYWORDS_MAX,
  PRODUCT_INTERESTS,
  INCLUSIONS_TEXT_MAX,
  EXPERIENCE_LEADERS,
  MAIN_INCLUSIONS,
  NOT_INCLUDED_OPTIONS,
  MEAL_TYPES,
  MEAL_FORMATS,
  TRANSPORT_TYPES,
  NOT_SUITABLE_FOR,
  SUITABILITY_TAGS,
  REFUND_POLICY_OPTIONS,
  REFUND_POLICY_UNITS,
} from "./wizardConstants";

const PRODUCT_CODE_MAX = 10;
const PRIMARY_CITY_MAX = 120;

const LooksGood = () => {
  usePartnerTranslation();
  return <span className="inline-flex items-center gap-1 font-semibold text-[#0F7A3B]">
    <Check className="h-3.5 w-3.5" strokeWidth={2.2} />{tr("Looks good")}</span>;
};

const contactErrorFor = (value) =>
  containsContactInfo(value) ? CONTACT_INFO_ERROR : "";

/* ---------- Helper that adds an "Other" option to a checklist + textarea ---------- */
const ListWithOther = ({
  values,
  onChange,
  options,
  testid,
  otherValue,
  onOtherChange,
  otherPlaceholder,
}) => {
  usePartnerTranslation();
  const otherSelected = values.includes("Other");
  const optionsWithOther = [...options, "Other"];
  const setTags = (next) => {
    if (!next.includes("Other")) onOtherChange("");
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <CompactCheckList
        values={values}
        onChange={setTags}
        options={optionsWithOther}
        testid={testid}
      />
      {otherSelected && (
        <TextArea
          value={otherValue}
          onChange={onOtherChange}
          placeholder={
            otherPlaceholder ||
            tr("Add custom items here. Press Enter for a new line.")
          }
          rows={3}
          testid={testid ? `${testid}-other` : undefined}
          maxLength={500}
        />
      )}
    </div>
  );
};

/* ---------- Meals repeater ---------- */
const MealsRepeater = ({ values = [], onChange }) => {
  usePartnerTranslation();
  const add = () =>
    onChange([
      ...values,
      { type: "Lunch", format: "Full meal" },
    ]);
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  const set = (i, key, v) =>
    onChange(values.map((m, idx) => (idx === i ? { ...m, [key]: v } : m)));

  return (
    <div className="space-y-2.5" data-testid="meals-repeater">
      {values.length === 0 && (
        <p className="text-xs italic text-[#9BA1AB]">{tr("No meals added yet — add one for each meal that's part of your tour.")}</p>
      )}
      {values.map((meal, i) => (
        <div
          key={i}
          data-testid={`meal-${i}`}
          className="relative grid grid-cols-1 gap-2 rounded-lg border border-[#E6E8EC] bg-white p-3 pe-10 sm:grid-cols-2"
        >
          <div>
            <label className="text-xs font-semibold text-[#6B7280]">{tr("Meal type")}</label>
            <SelectField
              value={meal.type}
              onChange={(v) => set(i, "type", v)}
              options={MEAL_TYPES}
              testid={`meal-${i}-type`}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6B7280]">{tr("Format")}</label>
            <SelectField
              value={meal.format}
              onChange={(v) => set(i, "format", v)}
              options={MEAL_FORMATS}
              testid={`meal-${i}-format`}
            />
          </div>
          <button
            type="button"
            onClick={() => remove(i)}
            data-testid={`meal-${i}-remove`}
            aria-label={tr("Remove meal")}
            className="absolute end-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-[#6B7280] hover:bg-[#FFEFEF] hover:text-[#9B0000]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        data-testid="meals-add"
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-dashed border-[#CFD2D8] bg-white px-3 text-xs font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
      >
        <Plus className="h-3.5 w-3.5" />{tr("Add meal")}</button>
    </div>
  );
};

/* ---------- Highlights (exactly 3 fields) ---------- */
const Highlights = ({ values = ["", "", ""], onChange, errors = [] }) => {
  usePartnerTranslation();
  const set = (i, v) =>
    onChange(values.map((x, idx) => (idx === i ? v.slice(0, HIGHLIGHT_MAX) : x)));
  return (
    <div className="space-y-4" data-testid="highlights-list">
      {Array.from({ length: HIGHLIGHT_COUNT }).map((_, i) => {
        const v = values[i] || "";
        const len = v.length;
        const ok = len >= HIGHLIGHT_MIN && len <= HIGHLIGHT_MAX;
        const err = errors[i] || contactErrorFor(v);
        return (
          <div
            key={i}
            data-testid={`highlight-${i}`}
            className="grid gap-2 sm:grid-cols-[120px_minmax(0,1fr)_150px] sm:items-center"
          >
            <div className="flex items-center">
              <span className="text-xs font-semibold text-[#6B7280]">{tr("Highlight ")}{i + 1}</span>
            </div>
            <div>
              <TextInput
                value={v}
                onChange={(val) => set(i, val)}
                placeholder={
                  i === 0
                    ? tr("Skip-the-line entry to the Colosseum (no waiting in queue)")
                    : i === 1
                      ? tr("Small group of max 12 with an English-speaking archaeologist")
                      : tr("Roman Forum and Palatine Hill included in the same ticket")
                }
                testid={`highlight-${i}-input`}
                maxLength={HIGHLIGHT_MAX}
                error={err}
                enforceNoContactInfo
              />
              {err && <p className="mt-1 text-xs font-medium text-[#9B0000]">{err}</p>}
            </div>
            <div className="flex min-h-10 items-center justify-end gap-3 text-xs sm:justify-start">
              {!err && ok && <LooksGood />}
              <span
                className={
                  len === 0
                    ? "text-[#9BA1AB]"
                    : ok
                      ? "text-[#0F6B2B]"
                      : "text-[#7A4E00]"
                }
              >
                {len} / {HIGHLIGHT_MAX}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Step1Basics = ({ data, update, errors = {} }) => {
  usePartnerTranslation();
  const titleLen = (data.title || "").length;
  const titleContactError = contactErrorFor(data.title);
  const titleOk = titleLen >= TITLE_MIN && titleLen <= TITLE_MAX;
  const titleHelper =
    titleContactError
      ? ""
      : titleLen === 0
      ? `Between ${TITLE_MIN} and ${TITLE_MAX} characters`
      : titleLen < TITLE_MIN
        ? `${TITLE_MIN - titleLen} more characters needed`
        : titleOk
          ? <LooksGood />
          : tr("Too long — please trim");

  const shortLen = (data.short_description || "").length;
  const shortContactError = contactErrorFor(data.short_description);
  const shortOk = shortLen >= SHORT_DESC_MIN && shortLen <= SHORT_DESC_MAX;
  const shortHelper =
    shortContactError
      ? ""
      : shortLen < SHORT_DESC_MIN
      ? `${SHORT_DESC_MIN - shortLen} more characters needed`
      : shortLen > SHORT_DESC_MAX
        ? tr("Too long — please trim")
        : <LooksGood />;

  const fullLen = (data.full_description || "").length;
  const fullContactError = contactErrorFor(data.full_description);
  const fullOk = fullLen >= FULL_DESC_MIN && fullLen <= FULL_DESC_MAX;
  const fullHelper =
    fullContactError
      ? ""
      : fullLen < FULL_DESC_MIN
      ? `${FULL_DESC_MIN - fullLen} more characters needed`
      : fullLen > FULL_DESC_MAX
        ? tr("Too long — please trim")
        : <LooksGood />;
  const referenceCodeError = errors.reference_code || contactErrorFor(data.reference_code);
  const primaryCityError = errors.primary_city || contactErrorFor(data.primary_city);
  const shortDescriptionError = errors.short_description || shortContactError;
  const fullDescriptionError = errors.full_description || fullContactError;
  const keywordsError = errors.keywords;
  const visitLocationsError = errors.visit_locations || (Array.isArray(data.visit_locations) && data.visit_locations.some(containsContactInfo) ? CONTACT_INFO_ERROR : "");
  const inclusionsError = errors.inclusions_other || contactErrorFor(data.inclusions_other);
  const notIncludedError = errors.not_included_other || contactErrorFor(data.not_included_other);
  const guestMustKnowError = errors.guest_must_know || contactErrorFor(data.guest_must_know);
  const customRefundValue = data.custom_refund_policy_value || "";

  return (
    <section data-testid="step-basics" className="space-y-8">
      <SectionHeader
        title={tr("Product basics")}
        subtitle={tr("Public details about your product.")}
      />

      <div className="grid gap-6 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <FieldRow
          label={tr("Title")}
          required
          testid="field-title"
          helper={titleHelper}
          counter={`${titleLen} / ${TITLE_MAX}`}
          error={errors.title || titleContactError}
          info={
            <>
              <span className="block">{tr("Example: ")}<em>{tr("Rome Colosseum Skip-the-Line Tour with an Archaeologist")}</em>
              </span>
              <span className="mt-1 block text-[#FFB6B6]">{tr("Avoid superlatives like \"Best\", \"Top\", \"Greatest\", \"Amazing\" — they make listings feel salesy.")}</span>
            </>
          }
        >
          <TextInput
            value={data.title}
            onChange={(v) => update("title", v.slice(0, TITLE_MAX))}
            placeholder={tr("Rome Colosseum Skip-the-Line Tour with an Archaeologist")}
            testid="input-title"
            error={errors.title || titleContactError}
            maxLength={TITLE_MAX}
            enforceNoContactInfo
          />
        </FieldRow>

        <FieldRow
          label={tr("Product code")}
          testid="field-reference-code"
          helper={tr("Optional — for your own bookkeeping.")}
          error={referenceCodeError}
          info={tr("Used only by you. Customers never see this code. Useful if you sell the same product across multiple platforms.")}
        >
          <TextInput
            value={data.reference_code}
            onChange={(v) => update("reference_code", v.slice(0, PRODUCT_CODE_MAX))}
            placeholder="ROM-COL-PVT"
            testid="input-reference-code"
            maxLength={PRODUCT_CODE_MAX}
            error={referenceCodeError}
            enforceNoContactInfo
          />
        </FieldRow>
      </div>

      <FieldRow
        label={tr("Primary city of this activity")}
        required
        testid="field-primary-city"
        counter={`${(data.primary_city || "").length} / ${PRIMARY_CITY_MAX}`}
        helper={tr("Start typing and pick the main city customers will associate with this activity. Country and region are saved when available.")}
        error={primaryCityError}
        info={tr("City results are powered by OpenStreetMap data. This helps Voocly place the experience correctly in search, reporting, and future admin tools.")}
      >
        <CityTextInput
          value={data.primary_city}
          onChange={(v) => update("primary_city", v.slice(0, PRIMARY_CITY_MAX))}
          placeholder={tr("Search city, e.g. Genoa, Italy")}
          testid="input-primary-city"
          error={primaryCityError}
        />
      </FieldRow>

      <FieldRow
        label={tr("Short description")}
        required
        testid="field-short-description"
        counter={`${shortLen} / ${SHORT_DESC_MAX}`}
        helper={shortHelper}
        error={shortDescriptionError}
        info={
          <span>{tr("One or two sentences that show up at the top of your listing. Customers decide in seconds — make it clear and concrete.")}<br />{tr("Example: ")}<em>{tr("Walk through 2,000 years of Roman history with a local archaeologist and skip the entry queue.")}</em>
          </span>
        }
      >
        <TextArea
          value={data.short_description}
          onChange={(v) => update("short_description", v)}
          placeholder={tr("Walk through 2,000 years of Roman history with a local archaeologist.")}
          testid="input-short-description"
          maxLength={SHORT_DESC_MAX}
          rows={3}
          error={shortDescriptionError}
          enforceNoContactInfo
        />
      </FieldRow>

      <FieldRow
        label={tr("Full description")}
        required
        testid="field-full-description"
        counter={`${fullLen} / ${FULL_DESC_MAX}`}
        helper={fullHelper}
        error={fullDescriptionError}
        info={
          <span>{tr("Tell the full story of your experience. Cover what makes it unique, who it’s best for, and what guests will see, eat, learn, feel, etc.")}</span>
        }
      >
        <TextArea
          value={data.full_description}
          onChange={(v) => update("full_description", v)}
          placeholder={tr("Start in front of the Arch of Constantine where your guide briefs you on what we'll see today…")}
          testid="input-full-description"
          maxLength={FULL_DESC_MAX}
          rows={8}
          error={fullDescriptionError}
          enforceNoContactInfo
        />
      </FieldRow>

      <FieldRow
        label={tr("Highlights")}
        required
        testid="field-highlights"
        error={errors.highlights}
        info={tr("The bite-sized headlines customers scan before reading the description. Be specific — say what makes your product different.")}
      >
        <p className="mb-3 text-xs text-[#6B7280]">{tr("Add ")}{HIGHLIGHT_COUNT}{tr(" highlights.")}</p>
        <Highlights
          values={data.highlights}
          onChange={(v) => update("highlights", v)}
          errors={errors.highlights_list || []}
        />
      </FieldRow>

      <FieldRow
        label={tr("Where will customers visit?")}
        required
        testid="field-visit-locations"
        helper={tr("Type a place name and pick a suggestion, or press Enter to add a custom one. {{value0}}–{{value1}} entries.", { value0: VISIT_LOCATIONS_MIN, value1: VISIT_LOCATIONS_MAX })}
        error={visitLocationsError}
        info={tr("Cities, neighbourhoods, attractions, parks, monuments — any place your customers will visit.")}
      >
        <LocationTagInput
          values={data.visit_locations}
          onChange={(v) => update("visit_locations", v)}
          placeholder={tr("Colosseum, Roman Forum, Palatine Hill…")}
          testid="tags-visit-locations"
          maxItems={VISIT_LOCATIONS_MAX}
          enforceNoContactInfo
        />
      </FieldRow>

      <FieldRow
        label={tr("Which interests does this product match?")}
        required
        testid="field-keywords"
        helper={tr("Select up to 5 interests that are central to the experience. These help customers discover relevant products.")}
        error={keywordsError}
        info={tr("Choose the interests that best describe this experience.")}
      >
        <CompactCheckList
          values={data.keywords}
          onChange={(v) => {
            if (v.length > KEYWORDS_MAX) {
              toast.error(tr("Select no more than 5 interests"));
              return;
            }
            update("keywords", v);
          }}
          testid="tags-keywords"
          // Saved values outside the current list stay visible so they can be unticked.
          options={[...PRODUCT_INTERESTS, ...(data.keywords || []).filter((k) => !PRODUCT_INTERESTS.includes(k))]}
          columns={3}
        />
      </FieldRow>

      <FieldRow
        label={tr("Who leads the experience?")}
        required
        testid="field-experience-leader"
        error={errors.experience_leader}
        info={tr("Select who your customer will meet.")}
      >
        <SelectField
          value={data.experience_leader}
          onChange={(v) => update("experience_leader", v)}
          options={EXPERIENCE_LEADERS}
          placeholder={tr("Pick a leader type")}
          testid="select-experience-leader"
          error={errors.experience_leader}
        />
      </FieldRow>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FieldRow
          label={tr("What's included")}
          testid="field-inclusions"
          info={tr("What customers get for the price they pay.")}
        >
          <BulletTextArea
            value={data.inclusions_other || ""}
            onChange={(v) => update("inclusions_other", v)}
            placeholder={tr("Skip-the-line entry\n2-hour expert guide\nBottled water")}
            rows={5}
            maxLength={INCLUSIONS_TEXT_MAX}
            testid="input-inclusions"
            error={inclusionsError}
            enforceNoContactInfo
          />
        </FieldRow>

        <FieldRow
          label={tr("What's not included")}
          testid="field-not-included"
          info={tr("What guests will need to pay for separately.")}
        >
          <BulletTextArea
            value={data.not_included_other || ""}
            onChange={(v) => update("not_included_other", v)}
            placeholder={tr("Hotel pickup\nLunch\nGratuities")}
            rows={5}
            maxLength={INCLUSIONS_TEXT_MAX}
            testid="input-not-included"
            error={notIncludedError}
            enforceNoContactInfo
          />
        </FieldRow>
      </div>

      <FieldRow
        label={tr("Meals")}
        testid="field-meals"
        helper={tr("Add a row for each meal that's part of the experience.")}
        info={tr("Helps customers know if it's snacks vs full meals, and what to expect (e.g. cooking class vs tasting menu).")}
      >
        <MealsRepeater
          values={data.meals}
          onChange={(v) => update("meals", v)}
        />
      </FieldRow>

      <FieldRow
        label={tr("Transportation type")}
        testid="field-transports"
        helper={tr("Pick all that apply across the tour.")}
        info={tr("If your tour uses different transport types at different points (e.g. ferry then bus), select all of them.")}
      >
        <CompactCheckList
          values={data.transports}
          onChange={(v) => update("transports", v)}
          options={TRANSPORT_TYPES}
          testid="chips-transports"
        />
      </FieldRow>

      <FieldRow
        label={tr("Anything guests must know before booking?")}
        testid="field-guest-must-know"
        helper={tr("Clothing, documents, weather, access limitations, etc.")}
        error={guestMustKnowError}
        info={tr("Examples: bring photo ID, comfortable walking shoes, modest dress code (for places of worship), tour runs in light rain.")}
      >
        <TextArea
          value={data.guest_must_know}
          onChange={(v) => update("guest_must_know", v)}
          placeholder={tr("You can mention about fitness level, mobility needs, dress code, house rules, etc.")}
          rows={4}
          testid="input-guest-must-know"
          maxLength={1500}
          error={guestMustKnowError}
          enforceNoContactInfo
        />
      </FieldRow>

      <FieldRow
        label={tr("Set the refund policy for this product")}
        required
        testid="field-refund-policy"
        error={errors.refund_policy_type}
        info={tr("This tells customers when a booking can be cancelled and refunded.")}
      >
        <RadioCards
          value={data.refund_policy_type || "standard_24_hours"}
          onChange={(v) => update("refund_policy_type", v)}
          options={REFUND_POLICY_OPTIONS}
          testid="radio-refund-policy"
        />
      </FieldRow>

      {(data.refund_policy_type || "standard_24_hours") === "custom" && (
        <FieldRow
          label={tr("Custom refund window")}
          required
          testid="field-custom-refund-policy"
          error={errors.custom_refund_policy_value || errors.custom_refund_policy_unit}
          helper={tr("Customers can cancel only before this window closes.")}
        >
          <div className="grid gap-3 rounded-lg border border-[#EAECEF] bg-[#FAFAFB] p-3 sm:grid-cols-[minmax(0,1fr)_180px]">
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#6B7280]">{tr("Customer can cancel their booking")}</label>
              <TextInput
                value={customRefundValue}
                onChange={(v) =>
                  update("custom_refund_policy_value", v.replace(/\D/g, "").slice(0, 2))
                }
                placeholder="24"
                testid="input-custom-refund-value"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={2}
                error={errors.custom_refund_policy_value}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#6B7280]">{tr("before activity start time")}</label>
              <SelectField
                value={data.custom_refund_policy_unit || "Hour(s)"}
                onChange={(v) => update("custom_refund_policy_unit", v)}
                options={REFUND_POLICY_UNITS}
                testid="select-custom-refund-unit"
                error={errors.custom_refund_policy_unit}
              />
            </div>
          </div>
        </FieldRow>
      )}
    </section>
  );
};

export default Step1Basics;
